// Incoming calls: a card with Accept / Decline on every page (and every open tab) while someone is calling. Answering
// or declining on one tab, the caller giving up, or the ring timing out removes it everywhere.
import { useCallback, useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { api } from "../../lib/api";
import { startTone, type CallNavState, type Ringing } from "../../lib/calls";
import { useLiveEvent } from "../../lib/live";
import { Avatar } from "../../components/ui";
import { Icon } from "../../components/icons";
import { useOrgShell } from "../org";

export function IncomingCalls() {
  const { org } = useOrgShell();
  const navigate = useNavigate();
  const inCall = Boolean(useMatch("/o/:slug/call/:callId"));
  const [ringing, setRinging] = useState<Ringing[]>([]);
  const remove = (callId: unknown) => setRinging((list) => list.filter((r) => r.callId !== callId));

  const load = useCallback(() => {
    api<{ ringing: Ringing[] }>(`/orgs/${org.slug}/calls/ringing`).then((r) => setRinging(r.ringing)).catch(() => undefined);
  }, [org.slug]);
  useEffect(() => { load(); }, [load]);

  useLiveEvent((e) => {
    if (e.type === "ready") load();
    else if (e.type === "call.ringing") {
      const r = e as unknown as Ringing;
      setRinging((list) => [...list.filter((x) => x.callId !== r.callId), r]);
      if (document.visibilityState !== "visible" && "Notification" in window && Notification.permission === "granted") {
        try { new Notification(`${r.caller.name} is calling`, { body: r.media === "video" ? "Video call" : "Audio call", tag: r.callId }); } catch { /* not allowed here */ }
      }
    } else if (e.type === "call.ring_stopped" || e.type === "call.ended") remove(e.callId);
  });

  // In case the "stopped" event never arrives, a card goes away on its own after the ring time.
  useEffect(() => {
    const timers = ringing.map((r) => setTimeout(() => remove(r.callId), (r.ringSeconds + 5) * 1000));
    return () => timers.forEach(clearTimeout);
  }, [ringing]);

  const sounding = ringing.length > 0 && !inCall;
  useEffect(() => (sounding ? startTone("incoming") : undefined), [sounding]);

  if (!ringing.length) return null;

  async function decline(r: Ringing) {
    remove(r.callId);
    await api(`/orgs/${org.slug}/calls/${r.callId}/decline`, { method: "POST" }).catch(() => undefined);
  }
  function accept(r: Ringing, media: "audio" | "video") {
    remove(r.callId);
    navigate(`/o/${org.slug}/call/${r.callId}`, { state: { media } satisfies CallNavState });
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3" role="region" aria-label="Incoming calls">
      {ringing.map((r) => {
        const title = r.conversationKind === "dm" ? r.caller.name : r.conversationName || `${r.caller.name} and ${r.people - 2} others`;
        return (
          <div key={r.callId} role="alertdialog" aria-label={`Incoming call from ${r.caller.name}`} className="animate-[pulse_2s_ease-in-out_1] rounded-2xl bg-slate-900 p-4 text-white shadow-2xl ring-1 ring-white/10">
            <div className="flex items-center gap-3">
              <span className="relative"><Avatar name={r.caller.name} userId={r.caller.id} /><span className="absolute -inset-1 animate-ping rounded-full ring-2 ring-emerald-400/60" aria-hidden="true" /></span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{title}</p>
                <p className="text-sm text-slate-300">{r.conversationKind === "dm" ? "" : `${r.caller.name} · `}{r.media === "video" ? "Video call" : "Audio call"}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => decline(r)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 py-2 text-sm font-semibold hover:bg-red-700">
                <Icon name="phone" className="h-4 w-4 rotate-[135deg]" />Decline
              </button>
              {r.media === "video" && (
                <button type="button" onClick={() => accept(r, "video")} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-sm font-semibold hover:bg-emerald-700">
                  <Icon name="video" className="h-4 w-4" />Video
                </button>
              )}
              <button type="button" onClick={() => accept(r, "audio")} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 py-2 text-sm font-semibold hover:bg-emerald-700">
                <Icon name="phone" className="h-4 w-4" />{r.media === "video" ? "Audio" : "Accept"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
